let CardBackRaster = {};

(function () {
    'use strict';

    // Card backs in the reveal fan are ROTATED, and that is the whole problem.
    //
    // The Cardbacks tab paints the identical art at nearly the identical size
    // and looks clean — because it is not rotated. A rotated card is composited
    // by transforming the decoded bitmap, and the decoded cardback is ~736px
    // wide going into a ~246px box: a 3x reduction performed by the compositor
    // with no mipmaps, which ALIASES (jagged frame lines, shimmering filigree).
    // Handing it an even bigger oversampled canvas made that worse, not better.
    //
    // So the texture is prepared at roughly the size it is actually shown, with
    // the big reduction done ONCE here by successive halving (the classic
    // anti-aliased resize) instead of in a single compositor step. The rotation
    // then resamples something close to 1:1 and stays clean.
    //
    // Presentation only: it re-paints art the element already declares and
    // never changes which art that is.

    // A little headroom over the display size so the rotation has something to
    // sample; much more than this simply reintroduces the aliasing.
    const TEXTURE_HEADROOM = 1.5;
    const MAX_TEXTURE_PX = 4096;
    const SOURCE_IMAGE_CACHE_LIMIT = 32;
    const SOURCE_IMAGE_LOAD_TIMEOUT_MS = 12000;
    const RETRY_DELAYS_MS = [1000, 3000, 8000, 20000, 30000];

    const sourceImages = new Map();
    const paintStates = new WeakMap();
    const activePaintOwners = new Set();
    let paintOwnerObserver = null;
    let lifecycleWakesInstalled = false;

    function hasArmedRetryOwner() {
        for (const element of activePaintOwners) {
            const state = paintStates.get(element);
            if (state && state.retryTimer !== null && element.isConnected) return true;
        }
        return false;
    }

    function wakeArmedRetries() {
        activePaintOwners.forEach((element) => {
            const state = paintStates.get(element);
            if (!state || state.retryTimer === null) return;
            if (!element.isConnected || backgroundUrl(element) !== state.url) {
                retirePaintState(element);
                return;
            }
            clearTimeout(state.retryTimer);
            state.retryTimer = null;
            void paint(element);
        });
        syncLifecycleWakes();
    }

    function wakeVisibleRetries() {
        if (document.visibilityState === 'visible') wakeArmedRetries();
    }

    function syncLifecycleWakes() {
        const shouldInstall = hasArmedRetryOwner();
        if (shouldInstall === lifecycleWakesInstalled) return;
        lifecycleWakesInstalled = shouldInstall;
        const method = shouldInstall ? 'addEventListener' : 'removeEventListener';
        window[method]('online', wakeArmedRetries);
        window[method]('pageshow', wakeArmedRetries);
        document[method]('visibilitychange', wakeVisibleRetries);
    }

    function stopPaintOwnerObserverWhenIdle() {
        if (activePaintOwners.size > 0 || !paintOwnerObserver) return;
        paintOwnerObserver.disconnect();
        paintOwnerObserver = null;
    }

    function sweepDetachedPaintOwners() {
        activePaintOwners.forEach((element) => {
            if (!element.isConnected) retirePaintState(element);
        });
        stopPaintOwnerObserverWhenIdle();
    }

    function trackPaintOwner(element) {
        activePaintOwners.add(element);
        if (paintOwnerObserver || typeof MutationObserver !== 'function') return;
        paintOwnerObserver = new MutationObserver(sweepDetachedPaintOwners);
        paintOwnerObserver.observe(document.documentElement || document, {
            childList: true,
            subtree: true,
        });
    }

    function untrackPaintOwner(element) {
        activePaintOwners.delete(element);
        stopPaintOwnerObserverWhenIdle();
        syncLifecycleWakes();
    }

    function backgroundUrl(element) {
        const inline = element?.style?.backgroundImage || '';
        const computed = inline || window.getComputedStyle?.(element)?.backgroundImage || '';
        const match = /url\((['"]?)(.*?)\1\)/.exec(computed);
        return match ? match[2] : '';
    }

    /* One cache entry per RESOURCE. An inline style keeps a path as written
       (`/assets/...`), a computed style reports it absolute, and a warm names
       it either way — all three are the same bytes. */
    function sourceKey(url) {
        try {
            return new URL(url, document.baseURI).href;
        } catch (_) {
            return url;
        }
    }

    function loadImage(source) {
        const url = sourceKey(source);
        const cached = sourceImages.get(url);
        if (cached) {
            // Map insertion order is the LRU order. A hit becomes newest.
            sourceImages.delete(url);
            sourceImages.set(url, cached);
            return cached;
        }

        let pending;
        pending = new Promise((resolve) => {
            const image = new Image();
            let settled = false;
            let timeout = null;
            const finish = (result) => {
                if (settled) return;
                settled = true;
                if (timeout !== null) clearTimeout(timeout);
                timeout = null;
                image.onload = null;
                image.onerror = null;
                resolve(result);
            };
            image.decoding = 'async';
            // Cardbacks are served from Convex file storage — a different
            // origin. Without this the image still DISPLAYS, so nothing looked
            // wrong for as long as the canvas was only ever painted; but the
            // canvas is TAINTED, and the first consumer to read it back
            // (UI.EnemyFan, which needs the sharpened texture as an image)
            // gets a SecurityError and silently keeps the aliased original.
            // Must be set before `src` or it has no effect.
            image.crossOrigin = 'anonymous';
            image.onload = () => finish(image);
            image.onerror = () => finish(null);
            timeout = setTimeout(() => {
                image.removeAttribute?.('src');
                finish(null);
            }, SOURCE_IMAGE_LOAD_TIMEOUT_MS);
            image.src = source;
        });
        sourceImages.set(url, pending);
        while (sourceImages.size > SOURCE_IMAGE_CACHE_LIMIT) {
            sourceImages.delete(sourceImages.keys().next().value);
        }
        void pending.then((image) => {
            // A failure or hung request is not an enduring truth. Leave the
            // element's authoritative CSS background intact and let the next
            // paint retry this exact source.
            if (!image && sourceImages.get(url) === pending) sourceImages.delete(url);
        });
        return pending;
    }

    // The texture size: the element's device pixels plus rotation headroom,
    // never larger than the art itself and never unbounded.
    function textureSize(box, dpr, sourceWidth, sourceHeight) {
        const ratio = Math.max(1, Number(dpr) || 1);
        let width = Math.max(1, Math.round(box.width * ratio * TEXTURE_HEADROOM));
        let height = Math.max(1, Math.round(box.height * ratio * TEXTURE_HEADROOM));
        const ceilingWidth = Math.min(sourceWidth || width, MAX_TEXTURE_PX);
        const ceilingHeight = Math.min(sourceHeight || height, MAX_TEXTURE_PX);
        const overflow = Math.max(width / ceilingWidth, height / ceilingHeight, 1);
        if (overflow > 1) {
            width = Math.round(width / overflow);
            height = Math.round(height / overflow);
        }
        return { width, height };
    }

    // `background-size: cover` in canvas terms.
    function coverRect(sourceWidth, sourceHeight, boxWidth, boxHeight) {
        const scale = Math.max(boxWidth / sourceWidth, boxHeight / sourceHeight);
        const width = sourceWidth * scale;
        const height = sourceHeight * scale;
        return { x: (boxWidth - width) / 2, y: (boxHeight - height) / 2, width, height };
    }

    // Halve repeatedly until within 2x of the target. A single drawImage that
    // reduces by 3x samples far too sparsely and keeps the jaggies it was
    // meant to remove.
    function reduceInSteps(source, targetWidth, targetHeight) {
        /* Where the engine honours `imageSmoothingQuality = 'high'` it already
           does a proper one-shot downsample, and each manual half-step stacks
           another low-pass on top of it — measured at 1.35x to 3.08x of the
           detail depending on engine. Past roughly 8x, though, that one-shot
           filter starts aliasing, so step down to that ratio and no further.
           Same probe and same limit as the card-art sampler, so cardbacks and
           card art cannot disagree. A cardback tile is ~4.9x, i.e. one draw. */
        const highQuality = !!(window.MoonfallRasterSampler
            && window.MoonfallRasterSampler.supportsHighQualityDownscale
            && window.MoonfallRasterSampler.supportsHighQualityDownscale());
        const oneDrawLimit = highQuality ? 8 : 2;
        let current = source;
        let width = source.naturalWidth || source.width;
        let height = source.naturalHeight || source.height;
        while (width > targetWidth * oneDrawLimit && height > targetHeight * oneDrawLimit) {
            width = Math.max(targetWidth, Math.round(width / 2));
            height = Math.max(targetHeight, Math.round(height / 2));
            const step = document.createElement('canvas');
            step.width = width;
            step.height = height;
            const stepContext = step.getContext('2d');
            if (!stepContext) return current;
            stepContext.imageSmoothingEnabled = true;
            stepContext.imageSmoothingQuality = 'high';
            stepContext.drawImage(current, 0, 0, width, height);
            current = step;
        }
        return current;
    }

    // Query, never firstElementChild: CardTilt prepends a `.card-glare` onto
    // every tilted card, so the texture does not stay the first child.
    function existingCanvas(element) {
        return element.querySelector(':scope > canvas[data-card-back-raster="true"]');
    }

    function removeCanvas(element) {
        const canvas = existingCanvas(element);
        if (canvas) {
            canvas.width = 0;
            canvas.height = 0;
            canvas.remove();
        }
        delete element.dataset.cardBackRasterSource;
    }

    function retirePaintState(element) {
        const state = paintStates.get(element);
        if (!state) {
            untrackPaintOwner(element);
            return;
        }
        state.retired = true;
        if (state.retryTimer !== null) clearTimeout(state.retryTimer);
        state.retryTimer = null;
        paintStates.delete(element);
        untrackPaintOwner(element);
    }

    function ownsPaintState(element, state) {
        return !state.retired && paintStates.get(element) === state;
    }

    function scheduleRetry(element, state) {
        if (!ownsPaintState(element, state)) return;
        if (!element.isConnected || backgroundUrl(element) !== state.url) {
            retirePaintState(element);
            return;
        }

        if (state.retryTimer !== null) clearTimeout(state.retryTimer);
        const delay = RETRY_DELAYS_MS[Math.min(state.retryIndex, RETRY_DELAYS_MS.length - 1)];
        state.retryIndex = Math.min(state.retryIndex + 1, RETRY_DELAYS_MS.length - 1);
        state.retryTimer = setTimeout(() => {
            state.retryTimer = null;
            if (!ownsPaintState(element, state)) return;
            if (!element.isConnected) {
                retirePaintState(element);
                return;
            }
            if (backgroundUrl(element) !== state.url) {
                retirePaintState(element);
            }
            void paint(element);
        }, delay);
        state.retryTimer.unref?.();
        trackPaintOwner(element);
        syncLifecycleWakes();
    }

    async function paintAttempt(element, state) {
        const { url } = state;

        // Asset-loading doctrine: the element's own authoritative CSS art stays
        // visible for the whole wait, so the back is never blank mid-load.
        const image = await loadImage(url);
        if (!ownsPaintState(element, state)) return false;
        if (!element.isConnected) {
            retirePaintState(element);
            return false;
        }
        if (backgroundUrl(element) !== url) {
            retirePaintState(element);
            void paint(element);
            return false;
        }
        if (!image || !(image.naturalWidth > 0) || !(image.naturalHeight > 0)) {
            scheduleRetry(element, state);
            return false;
        }
        const width = Number(element.offsetWidth) || 0;
        const height = Number(element.offsetHeight) || 0;
        if (!(width > 0) || !(height > 0)) {
            scheduleRetry(element, state);
            return false;
        }

        const texture = textureSize(
            { width, height },
            window.devicePixelRatio || 1,
            image.naturalWidth,
            image.naturalHeight,
        );

        const settled = existingCanvas(element);
        if (settled
            && settled.width === texture.width
            && settled.height === texture.height
            && element.dataset.cardBackRasterSource === url) {
            return true;
        }

        const canvas = settled || document.createElement('canvas');
        canvas.dataset.cardBackRaster = 'true';
        canvas.width = texture.width;
        canvas.height = texture.height;
        canvas.style.position = 'absolute';
        canvas.style.inset = '0';
        canvas.style.width = '100%';
        canvas.style.height = '100%';
        canvas.style.borderRadius = 'inherit';
        canvas.style.pointerEvents = 'none';

        const context = canvas.getContext('2d');
        if (!context) {
            scheduleRetry(element, state);
            return false;
        }
        try {
            context.imageSmoothingEnabled = true;
            context.imageSmoothingQuality = 'high';
            const reduced = reduceInSteps(image, texture.width, texture.height);
            const rect = coverRect(
                reduced.naturalWidth || reduced.width,
                reduced.naturalHeight || reduced.height,
                texture.width,
                texture.height,
            );
            context.clearRect(0, 0, texture.width, texture.height);
            context.drawImage(reduced, rect.x, rect.y, rect.width, rect.height);
        } catch (_) {
            scheduleRetry(element, state);
            return false;
        }

        if (!ownsPaintState(element, state)
            || !element.isConnected
            || backgroundUrl(element) !== url) {
            return false;
        }

        if (!canvas.isConnected) element.insertBefore(canvas, element.firstChild);
        element.dataset.cardBackRasterSource = url;
        state.retryIndex = 0;
        if (state.retryTimer !== null) clearTimeout(state.retryTimer);
        state.retryTimer = null;
        untrackPaintOwner(element);
        return true;
    }

    function paint(element) {
        if (!element) return Promise.resolve(false);
        const url = backgroundUrl(element);
        if (!url) {
            retirePaintState(element);
            removeCanvas(element);
            return Promise.resolve(false);
        }

        let state = paintStates.get(element);
        if (!state || state.url !== url) {
            retirePaintState(element);
            if (element.dataset.cardBackRasterSource !== url) removeCanvas(element);
            state = {
                url,
                retryIndex: 0,
                retryTimer: null,
                pending: null,
                retired: false,
            };
            paintStates.set(element, state);
        }

        if (state.pending) return state.pending;
        const width = Number(element.offsetWidth) || 0;
        const height = Number(element.offsetHeight) || 0;
        if (!(width > 0) || !(height > 0)) {
            scheduleRetry(element, state);
            return Promise.resolve(false);
        }
        if (state.retryTimer !== null) {
            clearTimeout(state.retryTimer);
            state.retryTimer = null;
        }

        const pending = paintAttempt(element, state);
        state.pending = pending;
        trackPaintOwner(element);
        void pending.finally(() => {
            if (ownsPaintState(element, state) && state.pending === pending) {
                state.pending = null;
                if (state.retryTimer === null) untrackPaintOwner(element);
            }
        });
        return pending;
    }

    function clear(element) {
        if (!element) return;
        retirePaintState(element);
        removeCanvas(element);
    }

    /**
     * Fetch and DECODE a back before any element shows it, into the same
     * source cache `paint()` draws from, so a board built afterwards paints
     * its piles and its fan in the task that builds it. The match loading
     * screen calls this while the server is still building the match
     * (ui/match-loading.js). Resolves false on failure; never rejects.
     */
    function warm(url) {
        const source = typeof url === 'string' ? url.trim() : '';
        if (!source) return Promise.resolve(false);
        return loadImage(source).then((image) => {
            if (!image) return false;
            if (typeof image.decode !== 'function') return true;
            // A loaded image with pixels is drawable even when decode()
            // rejects (asset-preloader.ts makes the same call).
            return Promise.resolve(image.decode()).then(() => true, () => true);
        });
    }

    CardBackRaster = { paint, clear, warm, textureSize, coverRect };
})();

export { CardBackRaster };
